import {ErrorFlow} from 'olympe';
import {stringWithOnlyFirstLetterInCapital} from '../utils/helpers';

/**
 * Singleton class controlling the Master Data
 */
export class MasterDataController {
    /**
     * Take a json string received by the database, and transform it into a JS object.
     * @param {string} jsonData
     * @param {boolean} showValue
     * @param {string} property
     * @return {[]}
     */
    static formatMasterData(jsonData, showValue, property) {
        try {
            const parsedData = JSON.parse(jsonData);
            const data = parsedData?.d?.results ?? parsedData;
            if (!Array.isArray(data)) { // data should be an array
                console.error('Your options are not an array. It should be a JSON array.');
                throw ErrorFlow.create(data, 500);
            } else if (data.length === 0) { // data should not be empty
                return [];
            } else { // format master data
                let result = [];
                if ('butxt' in data[0] && 'bukrs' in data[0]) { // Company Code
                    result = data.map((d) => {
                        return {
                            label: d.bukrs + ' - ' + d.butxt,
                            value: d.bukrs,
                        };
                    });
                } else if ('ekorg' in data[0] && 'ekotx' in data[0]) { // Purchase organization
                    result = data.map((d) => {
                        return {
                            label: d.ekorg + ' - ' + d.ekotx,
                            value: d.ekorg,
                        };
                    });
                } else if ('ekgrp' in data[0] && 'eknam' in data[0]) { // Purchasing group
                    result = data.map((d) => {
                        return {
                            label: d.ekgrp + ' - ' + d.eknam,
                            value: d.ekgrp,
                        };
                    });
                } else if ('werks' in data[0] && 'name1' in data[0]) { // Plant
                    result = data.map((d) => {
                        return {
                            label: d.werks + ' - ' + d.name1,
                            value: d.werks,
                            addressNumber: d.adrnr,
                        };
                    });
                } else if ('Term' in data[0] && 'NameVAT' in data[0] && 'Tagg' in data[0] && property === 'Payment Terms') { // Payment Terms
                    result = data.map((d) => {
                        const dayLimit = d['Tagg'] !== '0' ? ' - ' + d['Tagg'] : '';
                        return {
                            label: d['Term'] + ' - ' + d['NameVAT'] + dayLimit,
                            value: d['Term'],
                        };
                    });
                } else if ('parvw' in data[0] && 'lifnr' in data[0]) { // Ordering Address and Invoice Partner
                    result = data.map((d) => {
                        const description = d.parvw === 'OA' ? 'Ordering Address' :
                            d.parvw === 'PI' ? 'Invoice Partner' : 'Main Vendor';
                        return {
                            label: d.lifnr + ' - ' + description,
                            value: d.lifnr,
                        };
                    });
                } else if ('inco1' in data[0] && 'bezei' in data[0]) { // Incoterms
                    result = data.map((d) => {
                        return {
                            label: d.inco1 + ' - ' + d.bezei,
                            value: d.inco1,
                        };
                    });
                } else if ('WBSElemnt' in data[0] && 'PostShort1' in data[0] && property === 'WBS') { // Account number - WBS
                    result = data.map((d) => {
                        return {
                            label: d['WBSElemnt'] + ' - ' + d['PostShort1'],
                            value: d['WBSElemnt'],
                            status: stringWithOnlyFirstLetterInCapital(d['Status']),
                        };
                    });
                } else if ('kostl' in data[0] && 'name1' in data[0]) { // Account number - Cost Center
                    result = data.map((d) => {
                        return {
                            label: d.kostl + ' - ' + d.name1,
                            value: d.kostl,
                        };
                    });
                } else if ('ProfitCentr' in data[0] && 'ShortTxt' in data[0] && property === 'Profit Center') { // Account number - Profit Center
                    result = data.map((d) => {
                        return {
                            label: d['ProfitCentr'] + ' - ' + d['ShortTxt'],
                            value: d['ProfitCentr'],
                        };
                    });
                } else if ('saknr' in data[0] && 'txt50' in data[0]) { // GL account (from table)
                    result = data.map((d) => {
                        return {
                            label: d.saknr + ' - ' + d.txt50,
                            value: d.saknr,
                        };
                    });
                } else if ('GLAccNo' in data[0] && 'GLAccLngtxt' in data[0] && (property === 'GL Account' || property === 'GL Account filtered by WBS')) { // GL account (from MD API)
                    result = data.map((d) => {
                        return {
                            label: d['GLAccNo'] + ' - ' + d['GLAccLngtxt'],
                            value: d['GLAccNo'],
                        };
                    });
                } else if ('StorgLoc' in data[0] && 'DescStrorgLoc' in data[0] && property === 'Storage Location') { // Storage Location
                    result = data.map((d) => {
                        return {
                            label: d['StorgLoc'] + ' - ' + d['DescStrorgLoc'],
                            value: d['StorgLoc'],
                            lgort: d['StorgLoc'],
                        };
                    });
                } else if ('CurrencyKey' in data[0] && 'ShortTxt' in data[0] && property === 'Currency') { // Currency
                    result = data.map((d) => {
                        return {
                            label: d['CurrencyKey'] + ' - ' + d['ShortTxt'],
                            value: d['CurrencyKey'],
                        };
                    });
                } else if ('matnr' in data[0]) { // Item Code
                    result = data.map((d) => {
                        const description = d.maktx ? ` - ${d.maktx}` : '';
                        return {
                            label: `${d.matnr}${description}`,
                            value: d.matnr,
                        };
                    });
                } else if ('PurchsCode' in data[0] && 'NameVAT' in data[0] && property === 'Tax Code') { // Tax Code
                    result = data.map((d) => {
                        return {
                            label: d['PurchsCode'] + ' - ' + d['NameVAT'],
                            value: d['PurchsCode'],
                        };
                    });
                } else if ('meinh' in data[0] && 'umrez' in data[0] && 'umren' in data[0] && property === 'Unit of Measure') { // Unit of Measure
                    result = data.map((d) => {
                        return {
                            label: d['meinh'],
                            value: (parseInt(d['umrez'], 10) / parseInt(d['umren'], 10)).toString(10),
                        };
                    });
                }
                return result.filter((value, index, self) => // Remove duplicates
                    index === self.findIndex((t) => {
                        if (showValue) {
                            return t.value === value.value;
                        } else {
                            return t.label === value.label;
                        }
                    },
                    ),
                );
            }
        } catch (e) {
            console.error(e);
            throw e;
        }
    }

    /**
     * Fetch the master data from the database according
     * @param {object} getMasterData
     * @param {object} context
     * @param {string} property
     * @param {string} textForQuery - This text will be used for a search when performing the database query
     * @param {LabelValue} selectedCompanyCode
     * @param {LabelValue} selectedPlant
     * @param {LabelValue} selectedWBS
     * @param {string} materialNumber
     * @param {string} itemCodeSearchProp
     * @param {string} l3Category
     * @param {string} userId
     * @return {Promise<unknown>|Promise<string>}
     */
    static fetchMasterData(
        getMasterData,
        context,
        property,
        textForQuery,
        selectedCompanyCode,
        selectedPlant,
        selectedWBS,
        materialNumber,
        itemCodeSearchProp,
        l3Category,
        userId) {
        if (!getMasterData) {
            console.warn('Missing getMasterData, did you forget to pass the prop ?');
            return Promise.resolve(JSON.stringify([]));
        }

        return new Promise((resolve, reject) => {
            const [
                controlFlow,
                propertyInput,
                searchInput,
                selectedCompanyCodeInput,
                selectedPlantInput,
                selectedWBSInput,
                materialNumberInput,
                itemCodeSearchPropInput,
                l3CategoryInput,
                userIdInput,
            ] = getMasterData.getInputs();

            const [
                Success,
                result,
                Error,
            ] = getMasterData.getOutputs();

            const getMasterDataRunner = context.runner(getMasterData)
                .set(propertyInput, property)
                .set(searchInput, textForQuery);

            if (selectedCompanyCode) {
                getMasterDataRunner.set(selectedCompanyCodeInput, selectedCompanyCode.value);
            }
            if (selectedPlant) {
                getMasterDataRunner.set(selectedPlantInput, selectedPlant.value);
            }
            if (selectedWBS) {
                getMasterDataRunner.set(selectedWBSInput, selectedWBS.value);
            }
            if (materialNumber) {
                getMasterDataRunner.set(materialNumberInput, materialNumber);
            }
            if (itemCodeSearchProp) {
                getMasterDataRunner.set(itemCodeSearchPropInput, itemCodeSearchProp);
            }
            if (l3Category) {
                getMasterDataRunner.set(l3CategoryInput, l3Category);
            }

            getMasterDataRunner.set(userIdInput, userId);
            getMasterDataRunner.trigger(controlFlow);

            Promise.any([
                getMasterDataRunner.waitFor(Success),
                getMasterDataRunner.waitFor(Error),
            ]).then((outputFlow) => {
                if (outputFlow instanceof ErrorFlow) {
                    reject(ErrorFlow.create(getMasterDataRunner.get(result), outputFlow.getCode()));
                } else {
                    resolve(getMasterDataRunner.get(result));
                }
            }).catch(reject)
                .finally(() => getMasterDataRunner.destroy());
        });
    };
}
